'use client'

import { useState, useEffect } from 'react'
import { XIcon, CheckIcon } from '@phosphor-icons/react'
import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import { Textarea } from '@/ui/textarea'
import type { MediaKind } from '@/entries'
import type { MediaEditValues } from './types'

interface EditFormProps {
  type: MediaKind
  initialValues: {
    title: string
    genres: string
    description: string
    year: string
    director: string
    author: string
    website: string
    bpm: string
    key: string
  }
  onSave: (values: MediaEditValues) => void
  onCancel: () => void
  isSubmitting?: boolean
}

/**
 * Edit form for media preview fields
 */
export function EditForm({
  type,
  initialValues,
  onSave,
  onCancel,
  isSubmitting = false,
}: EditFormProps) {
  const [editTitle, setEditTitle] = useState(initialValues.title)
  const [editGenres, setEditGenres] = useState(initialValues.genres)
  const [editDescription, setEditDescription] = useState(
    initialValues.description,
  )
  const [editYear, setEditYear] = useState(initialValues.year)
  const [editDirector, setEditDirector] = useState(initialValues.director)
  const [editAuthor, setEditAuthor] = useState(initialValues.author)
  const [editWebsite, setEditWebsite] = useState(initialValues.website)
  const [editBpm, setEditBpm] = useState(initialValues.bpm)
  const [editKey, setEditKey] = useState(initialValues.key)

  // Reset form when initial values change
  useEffect(() => {
    setEditTitle(initialValues.title)
    setEditGenres(initialValues.genres)
    setEditDescription(initialValues.description)
    setEditYear(initialValues.year)
    setEditDirector(initialValues.director)
    setEditAuthor(initialValues.author)
    setEditWebsite(initialValues.website)
    setEditBpm(initialValues.bpm)
    setEditKey(initialValues.key)
  }, [initialValues])

  const handleSave = () => {
    const genresArray = editGenres
      .split(',')
      .map((g) => g.trim())
      .filter(Boolean)

    const values: MediaEditValues = {}
    if (type === 'artist' || type === 'label') {
      values.name = editTitle
    } else {
      values.title = editTitle
    }

    if (genresArray.length > 0) {
      values.genres = genresArray
    }

    if (type === 'artist') {
      values.bio = editDescription || undefined
    } else if (type === 'label') {
      values.profile = editDescription || undefined
      values.website = editWebsite || undefined
    } else {
      values.description = editDescription || undefined
    }

    if (editYear) {
      values.year = parseInt(editYear, 10) || undefined
    } else {
      values.year = undefined
    }

    if (type === 'movie') {
      values.director = editDirector || undefined
    }
    if (type === 'book') {
      values.author = editAuthor || undefined
    }
    if (type === 'release') {
      if (editBpm) values.bpm = parseInt(editBpm, 10) || undefined
      values.key = editKey || undefined
    }

    onSave(values)
  }

  return (
    <div className="space-y-3 p-3">
      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          {type === 'artist' || type === 'label' ? 'Name' : 'Title'}
        </label>
        <Input
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          className="h-8 text-sm"
          placeholder={type === 'artist' || type === 'label' ? 'Name' : 'Title'}
        />
      </div>

      {(type === 'movie' || type === 'book' || type === 'release') && (
        <div className="grid grid-cols-2 gap-2">
          {type === 'movie' && (
            <div>
              <label className="text-muted-foreground mb-1 block text-xs">
                Director
              </label>
              <Input
                value={editDirector}
                onChange={(e) => setEditDirector(e.target.value)}
                className="h-8 text-sm"
                placeholder="Director name"
              />
            </div>
          )}
          {type === 'book' && (
            <div>
              <label className="text-muted-foreground mb-1 block text-xs">
                Author
              </label>
              <Input
                value={editAuthor}
                onChange={(e) => setEditAuthor(e.target.value)}
                className="h-8 text-sm"
                placeholder="Author name"
              />
            </div>
          )}
          {(type === 'movie' || type === 'book' || type === 'release') && (
            <div>
              <label className="text-muted-foreground mb-1 block text-xs">
                Year
              </label>
              <Input
                type="number"
                value={editYear}
                onChange={(e) => setEditYear(e.target.value)}
                className="h-8 text-sm"
                placeholder="2024"
              />
            </div>
          )}
        </div>
      )}

      {type === 'label' && (
        <div>
          <label className="text-muted-foreground mb-1 block text-xs">
            Website
          </label>
          <Input
            value={editWebsite}
            onChange={(e) => setEditWebsite(e.target.value)}
            className="h-8 text-sm"
            placeholder="https://example.com"
          />
        </div>
      )}

      {(type === 'artist' ||
        type === 'label' ||
        type === 'release' ||
        type === 'movie') && (
        <div>
          <label className="text-muted-foreground mb-1 block text-xs">
            Genres (comma-separated)
          </label>
          <Input
            value={editGenres}
            onChange={(e) => setEditGenres(e.target.value)}
            className="h-8 text-sm"
            placeholder="Rock, Pop, Jazz"
          />
        </div>
      )}

      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          {type === 'artist'
            ? 'Bio'
            : type === 'label'
              ? 'Profile'
              : 'Description'}
        </label>
        <Textarea
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
          className="min-h-[60px] resize-none text-sm"
          placeholder={
            type === 'artist'
              ? 'Artist biography...'
              : type === 'label'
                ? 'Label description...'
                : 'Description...'
          }
        />
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isSubmitting}
          className="h-7 px-2 text-xs"
        >
          <XIcon className="mr-1 h-3.5 w-3.5" />
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={isSubmitting}
          className="h-7 px-2 text-xs"
        >
          <CheckIcon className="mr-1 h-3.5 w-3.5" />
          Save
        </Button>
      </div>
    </div>
  )
}
